import AsyncStorage from '@react-native-async-storage/async-storage';
import { ScopedAsyncStorage } from './ScopedAsyncStorage';

/**
 * Migration version key (stored globally, not scoped)
 */
const MIGRATION_VERSION_KEY = '@fimfam/internal/migration-version';

/**
 * Current migration version
 * Increment this when adding new migrations
 */
const CURRENT_MIGRATION_VERSION = 1;

/**
 * Legacy storage keys to migrate
 */
const LEGACY_KEYS = [
  '@family_members',
  '@calendar_member_mappings',
  'calendarPreferences',
  '@fimfam/theme-preference',
  '@fimfam/notification-preferences',
  '@fimfam/preferred-name',
  '@fimfam/profile-picture',
] as const;

/**
 * Migration result details
 */
export interface MigrationResult {
  success: boolean;
  migratedKeys: string[];
  skippedKeys: string[];
  errors: string[];
  version: number;
}

/**
 * StorageMigrationManager
 * Handles one-time migration of existing unscoped data to scoped format.
 * Runs once per user/device combination.
 */
export class StorageMigrationManager {
  private readonly scopedStorage: ScopedAsyncStorage;
  private readonly userId: string | null;
  private readonly deviceId: string;

  constructor(scopedStorage: ScopedAsyncStorage) {
    this.scopedStorage = scopedStorage;
    this.userId = scopedStorage.getUserId();
    this.deviceId = scopedStorage.getDeviceId();
  }

  /**
   * Check if migration is needed for the current scope
   */
  async needsMigration(): Promise<boolean> {
    try {
      const versionKey = this.buildMigrationVersionKey();
      const versionStr = await AsyncStorage.getItem(versionKey);
      
      if (!versionStr) {
        return true;
      }
      
      const version = parseInt(versionStr, 10);
      return isNaN(version) || version < CURRENT_MIGRATION_VERSION;
    } catch (error) {
      console.error('[StorageMigrationManager] Error checking migration status:', error);
      return true;
    }
  }

  /**
   * Run migration if needed
   * @returns Migration result with details
   */
  async migrateIfNeeded(): Promise<MigrationResult> {
    const needsMigration = await this.needsMigration();
    
    if (!needsMigration) {
      return {
        success: true,
        migratedKeys: [],
        skippedKeys: [],
        errors: [],
        version: CURRENT_MIGRATION_VERSION,
      };
    }

    return this.runMigration();
  }

  /**
   * Force run migration regardless of version
   * Useful for testing or manual intervention
   */
  async runMigration(): Promise<MigrationResult> {
    const result: MigrationResult = {
      success: true,
      migratedKeys: [],
      skippedKeys: [],
      errors: [],
      version: CURRENT_MIGRATION_VERSION,
    };

    console.log('[StorageMigrationManager] Starting migration for scope:', {
      userId: this.userId,
      deviceId: this.deviceId,
    });

    for (const legacyKey of LEGACY_KEYS) {
      try {
        const migrated = await this.migrateKey(legacyKey);
        if (migrated) {
          result.migratedKeys.push(legacyKey);
        } else {
          result.skippedKeys.push(legacyKey);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        result.errors.push(`${legacyKey}: ${errorMessage}`);
        result.success = false;
        console.error(`[StorageMigrationManager] Error migrating ${legacyKey}:`, error);
      }
    }

    // Mark migration as complete for this scope
    if (result.success || result.migratedKeys.length > 0) {
      try {
        const versionKey = this.buildMigrationVersionKey();
        await AsyncStorage.setItem(versionKey, String(CURRENT_MIGRATION_VERSION));
      } catch (error) {
        console.error('[StorageMigrationManager] Failed to save migration version:', error);
      }
    }

    console.log('[StorageMigrationManager] Migration complete:', result);
    return result;
  }

  /**
   * Migrate a single legacy key to scoped storage
   * @returns true if data was migrated, false if no data to migrate
   */
  private async migrateKey(legacyKey: string): Promise<boolean> {
    // Read legacy data
    const legacyData = await AsyncStorage.getItem(legacyKey);
    
    if (legacyData === null) {
      // No legacy data to migrate
      return false;
    }

    // Check if scoped key already has data (don't overwrite)
    const existingData = await this.scopedStorage.getItem(legacyKey);
    if (existingData !== null) {
      console.log(`[StorageMigrationManager] Skipping ${legacyKey} - scoped data already exists`);
      return false;
    }

    // Copy to scoped storage
    await this.scopedStorage.setItem(legacyKey, legacyData);
    
    console.log(`[StorageMigrationManager] Migrated ${legacyKey}`);
    return true;
  }

  /**
   * Remove all legacy keys after successful migration
   * Call this only after confirming migration was successful
   */
  async cleanupLegacyKeys(): Promise<void> {
    console.log('[StorageMigrationManager] Cleaning up legacy keys');
    
    for (const legacyKey of LEGACY_KEYS) {
      try {
        await AsyncStorage.removeItem(legacyKey);
      } catch (error) {
        console.error(`[StorageMigrationManager] Failed to remove legacy key ${legacyKey}:`, error);
      }
    }
  }

  /**
   * Build the migration version key for this scope
   */
  private buildMigrationVersionKey(): string {
    const userPart = this.userId ?? 'guest';
    return `${MIGRATION_VERSION_KEY}:${userPart}:${this.deviceId}`;
  }

  /**
   * Get the list of legacy keys that will be migrated
   */
  static getLegacyKeys(): readonly string[] {
    return LEGACY_KEYS;
  }

  /**
   * Get the current migration version
   */
  static getCurrentVersion(): number {
    return CURRENT_MIGRATION_VERSION;
  }
}
